import { useEffect, useMemo, useRef, useState } from "react";
import { useAnimals } from "../../../../hooks/useAnimals";
import { useBankTransfers } from "../../../../hooks/useBankTransfers";
import type { AnimalItem, ShareItem } from "../../../../mocks/animals";
import type { CustomerItem } from "../../../../mocks/customers";
import {
  buildGroupedHisseNo,
  getShareSlotNos,
} from "../../../../lib/salesFlow";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";
import MoneyInput from "../../../../components/form/MoneyInput";
import { formatTry, parseMoneyInput } from "../../../../lib/money";
import BackdropCloseContainer from "../../../../components/ui/BackdropCloseContainer";
import { isBankTransferActive } from "../../../../lib/bankTransferStore";
import { matchesOptionalDateRange } from "../../../../lib/dateFilters";

export interface SalePresetSelection {
  animalId?: number;
  slotNo?: number;
  defaultKesimUcreti?: number;
  defaultParcalamaUcreti?: number;
  defaultTasimaUcreti?: number;
}

export interface SaleFormData {
  customerId: number;
  animalId: number;
  slotNos: number[];
  hisseNo: string;
  hisseSayisi: number;
  birimSatisTutar: number;
  kesimUcreti: number;
  parcalamaUcreti: number;
  tasimaUcreti: number;
  tasimaVar: boolean;
  indirim: number;
  kisaNot: string;
  toplam: number;
  tarih: string;
  pesinatTutar: number;
  pesinatTip: "Nakit" | "Havale";
  pesinatHavaleNo: string;
  pesinatDekontNo: string;
  pesinatAciklama: string;
  fisNo: string;
  linkedBankTransferIds?: string[];
}

interface Props {
  customer: CustomerItem;
  presetSelection?: SalePresetSelection | null;
  onClose: () => void;
  onSave: (data: SaleFormData) => void;
}

const todayIso = new Date().toISOString().slice(0, 10);

function numVal(value: string) {
  return parseMoneyInput(value);
}

function InputLabel({
  label,
  required,
  hint,
}: {
  label: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="font-body text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-0.5 block">
      <span>
        {label}
        {required && <span className="text-farm-earth ml-0.5">*</span>}
      </span>
      {hint && (
        <span className="block mt-0.5 normal-case tracking-normal font-normal text-[10px] text-farm-muted/80">
          {hint}
        </span>
      )}
    </label>
  );
}

function SummaryRow({
  label,
  value,
  accent,
  danger,
}: {
  label: string;
  value: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-farm-muted">{label}</span>
      <span
        className={`font-semibold ${
          danger
            ? "text-red-600"
            : accent
            ? "text-farm-earth"
            : "text-farm-brown"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function getAnimalSoldShares(animal: AnimalItem) {
  return (animal.hisseler ?? []).filter(
    (share) => getShareSlotNos(share).length > 0
  );
}

function getSuggestedSaleTotal(
  animal: AnimalItem | null,
  selectedSlotCount: number
) {
  if (!animal || selectedSlotCount <= 0) return 0;

  const soldShares = getAnimalSoldShares(animal);
  if (soldShares.length === 0) return 0;

  const perSlotAmounts = soldShares
    .map((share) => {
      const slotCount = Math.max(
        1,
        getShareSlotNos(share).length || share.hisseSayisi || 1
      );
      return share.tutar / slotCount;
    })
    .filter((value) => Number.isFinite(value) && value > 0);

  if (perSlotAmounts.length === 0) return 0;

  const avgPerSlot =
    perSlotAmounts.reduce((sum, value) => sum + value, 0) /
    perSlotAmounts.length;

  return Math.round(avgPerSlot * selectedSlotCount);
}

function getBaseServiceValue(
  presetValue: number | undefined,
  animalValue: number | undefined
) {
  return Math.max(0, presetValue ?? animalValue ?? 0);
}

export default function SatisEkleDrawer({
  customer,
  presetSelection,
  onClose,
  onSave,
}: Props) {
  const { animals } = useAnimals();
  const { transfers } = useBankTransfers();

  const availableAnimals = useMemo<AnimalItem[]>(
    () =>
      animals.filter((animal) => {
        const soldSlotCount = (animal.hisseler ?? []).reduce(
          (sum, share) => sum + getShareSlotNos(share).length,
          0
        );

        return (
          (animal.durum === "Aktif" || animal.durum === "Kesim Bekliyor") &&
          soldSlotCount < animal.toplamHisse
        );
      }),
    [animals]
  );

  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);

  const [toplamSatisTutar, setToplamSatisTutar] = useState("");
  const [kesimUcreti, setKesimUcreti] = useState("");
  const [parcalamaUcreti, setParcalamaUcreti] = useState("");
  const [tasimaUcreti, setTasimaUcreti] = useState("");
  const [tasimaVar, setTasimaVar] = useState(false);
  const [indirim, setIndirim] = useState("0");
  const [kisaNot, setKisaNot] = useState("");
  const [tarih, setTarih] = useState(todayIso);
  const [fisNo, setFisNo] = useState("");
  const [pesinatTutar, setPesinatTutar] = useState("");
  const [pesinatTip, setPesinatTip] = useState<"Nakit" | "Havale">("Nakit");
  const [pesinatDekontNo, setPesinatDekontNo] = useState("");
  const [pesinatAciklama, setPesinatAciklama] = useState("");
  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>([]);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");
  const [error, setError] = useState("");

  const saleTouchedRef = useRef(false);
  const kesimTouchedRef = useRef(false);
  const parcalamaTouchedRef = useRef(false);
  const tasimaTouchedRef = useRef(false);
  const appliedPresetKeyRef = useRef("");

  const selectedAnimal = useMemo(
    () =>
      availableAnimals.find((animal) => animal.id === selectedAnimalId) ?? null,
    [availableAnimals, selectedAnimalId]
  );

  const usedSlotNumbers = useMemo<number[]>(() => {
    if (!selectedAnimal) return [];

    return selectedAnimal.hisseler
      .flatMap((share: ShareItem) => getShareSlotNos(share))
      .filter((value) => value > 0)
      .sort((a, b) => a - b);
  }, [selectedAnimal]);

  const availableSlots = useMemo<number[]>(() => {
    if (!selectedAnimal) return [];

    const used = new Set(usedSlotNumbers);
    const slots: number[] = [];

    for (let i = 1; i <= selectedAnimal.toplamHisse; i++) {
      if (!used.has(i)) {
        slots.push(i);
      }
    }

    return slots;
  }, [selectedAnimal, usedSlotNumbers]);

  const selectedSlotCount = selectedSlots.length;

  const suggestedSaleTotal = useMemo(
    () => getSuggestedSaleTotal(selectedAnimal, selectedSlotCount),
    [selectedAnimal, selectedSlotCount]
  );

  const baseKesim = getBaseServiceValue(
    presetSelection?.defaultKesimUcreti,
    selectedAnimal?.defaultKesimUcreti
  );
  const baseParcalama = getBaseServiceValue(
    presetSelection?.defaultParcalamaUcreti,
    selectedAnimal?.defaultParcalamaUcreti
  );
  const baseTasima = getBaseServiceValue(
    presetSelection?.defaultTasimaUcreti,
    selectedAnimal?.defaultTasimaUcreti
  );

  const suggestedKesim =
    selectedSlotCount > 0 ? baseKesim * selectedSlotCount : baseKesim;
  const suggestedParcalama =
    selectedSlotCount > 0 ? baseParcalama * selectedSlotCount : baseParcalama;
  const suggestedTasima =
    selectedSlotCount > 0 ? baseTasima * selectedSlotCount : baseTasima;

  function resetDirtyFlags() {
    saleTouchedRef.current = false;
    kesimTouchedRef.current = false;
    parcalamaTouchedRef.current = false;
    tasimaTouchedRef.current = false;
  }

  function applyFreshDefaults(nextAnimalId: number | null) {
    resetDirtyFlags();
    setError("");
    setSelectedAnimalId(nextAnimalId);
    setSelectedSlots([]);
    setToplamSatisTutar("");
    setKesimUcreti("");
    setParcalamaUcreti("");
    setTasimaUcreti("");
    setTasimaVar(false);
    setIndirim("0");
    setPesinatTutar("");
    setPesinatTip("Nakit");
    setPesinatDekontNo("");
    setPesinatAciklama("");
    setSelectedTransferIds([]);
    setTransferDateFrom("");
    setTransferDateTo("");
    setTransferDescriptionFilter("");
  }

  useEffect(() => {
    const presetKey = `${presetSelection?.animalId ?? ""}-${presetSelection?.slotNo ?? ""}`;

    if (!presetSelection?.animalId || !presetSelection?.slotNo) return;
    if (appliedPresetKeyRef.current === presetKey) return;

    const presetAnimal =
      availableAnimals.find((animal) => animal.id === presetSelection.animalId) ??
      null;

    if (!presetAnimal) return;

    appliedPresetKeyRef.current = presetKey;
    applyFreshDefaults(presetAnimal.id);
  }, [availableAnimals, presetSelection]);

  useEffect(() => {
    if (!selectedAnimal) return;

    if (
      presetSelection?.animalId === selectedAnimal.id &&
      presetSelection?.slotNo &&
      availableSlots.includes(presetSelection.slotNo)
    ) {
      setSelectedSlots((prev) => {
        if (prev.length === 0) {
          return [presetSelection.slotNo!];
        }

        return prev.filter((slotNo) => availableSlots.includes(slotNo));
      });
      return;
    }

    setSelectedSlots((prev) =>
      prev.filter((slotNo) => availableSlots.includes(slotNo))
    );
  }, [availableSlots, presetSelection, selectedAnimal]);

  useEffect(() => {
    if (!selectedAnimal || selectedSlotCount === 0) {
      if (!saleTouchedRef.current) setToplamSatisTutar("");
      if (!kesimTouchedRef.current) setKesimUcreti("");
      if (!parcalamaTouchedRef.current) setParcalamaUcreti("");
      if (!tasimaTouchedRef.current) setTasimaUcreti("");
      return;
    }

    if (!saleTouchedRef.current) {
      setToplamSatisTutar(
        suggestedSaleTotal > 0 ? String(suggestedSaleTotal) : "0"
      );
    }

    if (!kesimTouchedRef.current) {
      setKesimUcreti(suggestedKesim > 0 ? String(suggestedKesim) : "0");
    }

    if (!parcalamaTouchedRef.current) {
      setParcalamaUcreti(
        suggestedParcalama > 0 ? String(suggestedParcalama) : "0"
      );
    }

    if (!tasimaTouchedRef.current) {
      setTasimaUcreti(suggestedTasima > 0 ? String(suggestedTasima) : "0");
    }
  }, [
    selectedAnimal,
    selectedSlotCount,
    suggestedSaleTotal,
    suggestedKesim,
    suggestedParcalama,
    suggestedTasima,
  ]);

  function handleAnimalChange(value: string) {
    const nextId = value ? Number(value) : null;
    applyFreshDefaults(nextId);
  }

  function toggleSlot(slotNo: number) {
    setSelectedSlots((prev) =>
      prev.includes(slotNo)
        ? prev.filter((value) => value !== slotNo)
        : [...prev, slotNo].sort((a, b) => a - b)
    );
  }

  function restoreSuggestedValues() {
    resetDirtyFlags();

    setToplamSatisTutar(suggestedSaleTotal > 0 ? String(suggestedSaleTotal) : "0");
    setKesimUcreti(suggestedKesim > 0 ? String(suggestedKesim) : "0");
    setParcalamaUcreti(
      suggestedParcalama > 0 ? String(suggestedParcalama) : "0"
    );
    setTasimaUcreti(suggestedTasima > 0 ? String(suggestedTasima) : "0");
  }

  const hisseSayisi = selectedSlots.length;
  const toplamSatis = numVal(toplamSatisTutar);
  const toplamKesim = numVal(kesimUcreti);
  const toplamParcalama = numVal(parcalamaUcreti);
  const toplamTasima = numVal(tasimaUcreti);
  const toplamIndirim = numVal(indirim);

  const toplam =
    toplamSatis + toplamKesim + toplamParcalama + toplamTasima - toplamIndirim;

  const pesinat = numVal(pesinatTutar);
  const bakiye = toplam - pesinat;

  function formatTransferDate(value?: string) {
    if (!value) return "—";
    const [year, month, day] = value.slice(0, 10).split("-");
    return year && month && day ? `${day}.${month}.${year}` : value;
  }

  function containsTransferText(value: string | undefined, query: string) {
    if (!query.trim()) return true;
    return String(value ?? "")
      .toLocaleLowerCase("tr-TR")
      .includes(query.trim().toLocaleLowerCase("tr-TR"));
  }

  const activeBankTransfers = useMemo(
    () =>
      transfers
        .filter(isBankTransferActive)
        .filter((item) => matchesOptionalDateRange(item.tarih, transferDateFrom, transferDateTo))
        .filter(
          (item) =>
            containsTransferText(item.aciklama, transferDescriptionFilter) ||
            containsTransferText(item.fisNo, transferDescriptionFilter)
        ),
    [transfers, transferDateFrom, transferDateTo, transferDescriptionFilter]
  );

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function addSelectedTransfersToSale() {
    const selected = activeBankTransfers.filter((item) => selectedTransferIds.includes(item.id));
    if (selected.length === 0) {
      setError("Bağlamak için en az bir havale seç.");
      return;
    }

    const total = Math.round(selected.reduce((sum, item) => sum + Number(item.tutar || 0), 0));
    setPesinatTip("Havale");
    setPesinatTutar(String(total));
    setPesinatDekontNo(selected.map((item) => item.fisNo).join(", "));
    setTransferPickerOpen(false);
    setError("");
  }

  const canSave =
    selectedAnimal !== null &&
    selectedSlots.length > 0 &&
    Boolean(tarih) &&
    (pesinatTip !== "Havale" ||
      pesinat === 0 ||
      pesinatDekontNo.trim().length > 0);

  const hisseEtiketi =
    selectedAnimal && selectedSlots.length > 0
      ? buildGroupedHisseNo(selectedAnimal.siraNo, selectedSlots)
      : "—";

  function handleSave() {
    if (!selectedAnimal) return;

    if (selectedSlots.length === 0) {
      setError("En az 1 hisse seçmelisiniz.");
      return;
    }

    if (!tarih) {
      setError("Tarih zorunludur.");
      return;
    }

    if (toplam < 0) {
      setError("Genel toplam eksi olamaz.");
      return;
    }

    if (pesinatTip === "Havale" && pesinat > 0 && !pesinatDekontNo.trim()) {
      setError("Kapora tipi Havale ise havale / dekont no zorunludur.");
      return;
    }

    setError("");

    onSave({
      customerId: customer.id,
      animalId: selectedAnimal.id,
      slotNos: [...selectedSlots].sort((a, b) => a - b),
      hisseNo: buildGroupedHisseNo(selectedAnimal.siraNo, selectedSlots),
      hisseSayisi: selectedSlots.length,
      birimSatisTutar: toplamSatis,
      kesimUcreti: toplamKesim,
      parcalamaUcreti: toplamParcalama,
      tasimaUcreti: toplamTasima,
      tasimaVar,
      indirim: toplamIndirim,
      kisaNot,
      toplam,
      tarih,
      pesinatTutar: pesinat,
      pesinatTip,
      pesinatHavaleNo: pesinatDekontNo,
      pesinatDekontNo,
      pesinatAciklama,
      fisNo,
      linkedBankTransferIds: selectedTransferIds,
    });
  }

  return (
    <>
      <BackdropCloseContainer
        onClose={onClose}
        className="fixed inset-0 bg-black/30 z-40"
      />

      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[520px] bg-white shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-warm flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center text-farm-earth">
              <i className="ri-add-circle-line text-base" />
            </div>
            <span className="font-heading font-bold text-sm text-farm-brown">
              Satış Ekle
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-farm-muted hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div className="rounded-farm bg-farm-cream border border-farm-border px-3 py-2">
            <p className="font-body text-[11px] text-farm-muted">Müşteri</p>
            <p className="font-body text-sm font-semibold text-farm-brown">
              {customer.adSoyad}
            </p>
            <p className="font-body text-xs text-farm-muted mt-0.5">
              {customer.telefon}
            </p>
          </div>

          <div>
            <InputLabel label="Hayvan" required />
            <select
              value={selectedAnimalId ?? ""}
              onChange={(e) => handleAnimalChange(e.target.value)}
              className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-sm text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
            >
              <option value="">Hayvan seçin</option>
              {availableAnimals.map((animal) => {
                const usedSlotCount = (animal.hisseler ?? []).reduce(
                  (sum, share) => sum + getShareSlotNos(share).length,
                  0
                );
                const kalanHisse = animal.toplamHisse - usedSlotCount;

                return (
                  <option key={animal.id} value={animal.id}>
                    Küpe {getDisplayKupeNo(animal)} • Sipariş {getDisplaySiparisNo(
                      animal
                    )} • {kalanHisse} boş hisse
                  </option>
                );
              })}
            </select>
          </div>

          {selectedAnimal && (
            <div>
              <InputLabel label="Seçilecek Hisseler" required />
              <div className="flex flex-wrap gap-2">
                {availableSlots.map((slotNo) => {
                  const active = selectedSlots.includes(slotNo);

                  return (
                    <button
                      key={slotNo}
                      type="button"
                      onClick={() => toggleSlot(slotNo)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border cursor-pointer whitespace-nowrap ${
                        active
                          ? "bg-farm-earth text-white border-farm-earth"
                          : "bg-farm-cream text-farm-brown border-farm-border hover:border-farm-earth/40"
                      }`}
                    >
                      Hisse {slotNo}
                    </button>
                  );
                })}
              </div>

              {selectedSlots.length > 0 && (
                <p className="mt-2 text-xs text-farm-muted">
                  Seçilen: <span className="font-semibold text-farm-brown">{selectedSlots.join(", ")}</span>
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel label="Tarih" required />
              <input
                type="date"
                value={tarih}
                onChange={(e) => setTarih(e.target.value)}
                className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div>
              <InputLabel label="Fiş No" />
              <input
                type="text"
                value={fisNo}
                onChange={(e) => setFisNo(e.target.value)}
                placeholder="OPS-2026-0001"
                className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <InputLabel
                label="Toplam Satış Tutarı"
                required
                hint="0 olabilir. Elle yazıldıktan sonra sistem geri değiştirmez."
              />
              <button
                type="button"
                onClick={restoreSuggestedValues}
                disabled={selectedSlotCount === 0}
                className="px-2.5 py-1 rounded-full border border-farm-border text-farm-muted text-[10px] font-semibold hover:bg-farm-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Varsayılanı getir
              </button>
            </div>

            <MoneyInput
              value={toplamSatisTutar}
              onChange={(value) => {
                saleTouchedRef.current = true;
                setToplamSatisTutar(value);
              }}
              placeholder={String(suggestedSaleTotal)}
            />

            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-farm-muted">
              <span>Seçili hisse: {selectedSlotCount || 0}</span>
              {suggestedSaleTotal > 0 && (
                <span>Önerilen satış: {formatTry(suggestedSaleTotal)}</span>
              )}
              {saleTouchedRef.current && (
                <span className="text-farm-earth font-semibold">Elle kilitlendi</span>
              )}
            </div>
          </div>

          <div>
            <InputLabel label="Satış Etiketi" />
            <div className="h-[42px] px-3 flex items-center rounded-farm bg-farm-cream border border-farm-border font-body text-xs font-semibold text-farm-brown">
              {hisseEtiketi}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel label="Toplam Kesim" />
              <MoneyInput
                value={kesimUcreti}
                onChange={(value) => {
                  kesimTouchedRef.current = true;
                  setKesimUcreti(value);
                }}
                placeholder={String(suggestedKesim)}
              />
            </div>

            <div>
              <InputLabel label="Toplam Parçalama" />
              <MoneyInput
                value={parcalamaUcreti}
                onChange={(value) => {
                  parcalamaTouchedRef.current = true;
                  setParcalamaUcreti(value);
                }}
                placeholder={String(suggestedParcalama)}
              />
            </div>

            <div>
              <InputLabel label="Toplam Taşıma" />
              <MoneyInput
                value={tasimaUcreti}
                onChange={(value) => {
                  tasimaTouchedRef.current = true;
                  setTasimaUcreti(value);
                }}
                placeholder={String(suggestedTasima)}
              />
            </div>

            <div className="flex items-end">
              <label className="w-full flex items-center gap-2 px-3 py-3 rounded-farm border border-farm-border bg-farm-cream cursor-pointer">
                <input
                  type="checkbox"
                  checked={tasimaVar}
                  onChange={(e) => setTasimaVar(e.target.checked)}
                  className="accent-[#8b5e3c]"
                />
                <span className="font-body text-xs font-semibold text-farm-brown">
                  Taşıma Var
                </span>
              </label>
            </div>

            <div>
              <InputLabel label="Toplam İndirim" />
              <MoneyInput value={indirim} onChange={setIndirim} placeholder="0" />
            </div>
          </div>

          <div>
            <InputLabel label="Satış Notu" />
            <textarea
              value={kisaNot}
              onChange={(e) => setKisaNot(e.target.value)}
              placeholder="İsteğe bağlı satış notu"
              rows={3}
              className="w-full resize-none bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
            />
          </div>

          <div className="border border-farm-border rounded-farm bg-farm-warm p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-body text-xs font-semibold text-farm-brown">
                Satış Anında Kapora
              </p>
              <span className="text-[10px] text-farm-muted">
                İstenirse toplamı geçebilir, eksi bakiye oluşur
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <InputLabel label="Kapora Tutarı" />
                <MoneyInput value={pesinatTutar} onChange={setPesinatTutar} />
              </div>

              <div>
                <InputLabel label="Kapora Tipi" />
                <div className="grid grid-cols-2 gap-2">
                  {(["Nakit", "Havale"] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPesinatTip(item)}
                      className={`h-[42px] rounded-farm text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                        pesinatTip === item
                          ? item === "Nakit"
                            ? "bg-farm-green text-white border-farm-green"
                            : "bg-sky-600 text-white border-sky-600"
                          : "bg-farm-cream text-farm-brown border-farm-border"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {pesinatTip === "Havale" && (
              <div className="rounded-farm border border-sky-100 bg-sky-50/60 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <InputLabel label="Havale Bağla" />
                    <p className="text-[11px] text-farm-muted">
                      Aktif havalelerden bir veya birkaçını seçip kapora tutarına aktar.
                    </p>
                  </div>
                  <button type="button" onClick={() => setTransferPickerOpen(true)} className="h-9 rounded-xl bg-sky-600 px-3 text-xs font-semibold text-white hover:bg-sky-700">
                    Havale Bağla
                  </button>
                </div>
                {selectedTransferIds.length > 0 && (
                  <div className="mt-2 rounded-xl border border-sky-100 bg-white px-3 py-2 text-[11px] text-sky-700">
                    {selectedTransferIds.length} havale seçildi. Satış kaydedilince bu kayıtlar otomatik dolduruldu olarak pasife alınır.
                  </div>
                )}
              </div>
            )}

            <div>
              <InputLabel
                label="Havale / Dekont No"
                hint="Nakit için opsiyonel, havale için kapora varsa zorunlu."
              />
              <input
                type="text"
                value={pesinatDekontNo}
                onChange={(e) => setPesinatDekontNo(e.target.value)}
                placeholder="HVL-2026-0001 / Dekont no"
                className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>

            <div>
              <InputLabel label="Kapora Açıklama" />
              <input
                type="text"
                value={pesinatAciklama}
                onChange={(e) => setPesinatAciklama(e.target.value)}
                placeholder="Kapora açıklaması"
                className="w-full bg-farm-cream border border-farm-border rounded-farm px-3 py-2.5 font-body text-xs text-farm-brown focus:outline-none focus:border-farm-earth transition-colors"
              />
            </div>
          </div>

          <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3 space-y-1.5">
            <SummaryRow label="Seçilen hisse" value={String(hisseSayisi)} />
            <SummaryRow label="Toplam satış" value={formatTry(toplamSatis)} />
            <SummaryRow
              label="Toplam hizmetler"
              value={formatTry(toplamKesim + toplamParcalama + toplamTasima)}
            />
            <SummaryRow label="Taşıma işareti" value={tasimaVar ? "Var" : "Yok"} />
            <SummaryRow label="İndirim" value={formatTry(toplamIndirim)} />
            <SummaryRow label="Kapora" value={formatTry(pesinat)} accent />
            <div className="flex items-center justify-between text-sm pt-1 border-t border-farm-border/70">
              <span className="font-semibold text-farm-brown">Genel Toplam</span>
              <span className="font-bold text-farm-earth">{formatTry(toplam)}</span>
            </div>
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="font-semibold text-farm-brown">Kalan / Bakiye</span>
              <span
                className={`font-bold ${
                  bakiye < 0
                    ? "text-red-600"
                    : bakiye > 0
                    ? "text-farm-earth"
                    : "text-farm-green"
                }`}
              >
                {formatTry(bakiye)}
              </span>
            </div>
            {bakiye < 0 && (
              <p className="text-[11px] text-red-600 pt-1">
                Kapora toplamı geçti. Kayıt eksi bakiye ile açılacak.
              </p>
            )}
          </div>

          {transferPickerOpen && (
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-3">
              <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                  <div>
                    <h4 className="text-sm font-bold text-gray-800">Aktif Havale Bağla</h4>
                    <p className="text-[10px] text-gray-500">Boşta duran aktif havaleleri satış kaporasına bağlar. Birden fazla kutucuğu seçebilirsin.</p>
                  </div>
                  <button type="button" onClick={() => setTransferPickerOpen(false)} className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100"><i className="ri-close-line" /></button>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <input type="date" value={transferDateFrom} onChange={(event) => setTransferDateFrom(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" title="Başlangıç tarihi" />
                  <input type="date" value={transferDateTo} onChange={(event) => setTransferDateTo(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" title="Bitiş tarihi" />
                  <input value={transferDescriptionFilter} onChange={(event) => setTransferDescriptionFilter(event.target.value)} placeholder="Açıklama / Fiş No içinde ara" className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" />
                </div>
                <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
                  {activeBankTransfers.length === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-gray-500">Aktif havale yok.</div>
                  ) : (
                    activeBankTransfers.map((item) => {
                      const checked = selectedTransferIds.includes(item.id);
                      return (
                        <button key={item.id} type="button" onClick={() => toggleTransferSelection(item.id)} className={checked ? "grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 bg-sky-50 px-3 py-2.5 text-left text-xs hover:bg-sky-50" : "grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50"}>
                          <span className="pt-0.5"><span className={checked ? "flex h-4 w-4 items-center justify-center rounded border border-sky-600 bg-sky-600 text-white" : "flex h-4 w-4 items-center justify-center rounded border border-gray-300 bg-white"}>{checked && <i className="ri-check-line text-xs" />}</span></span>
                          <span className="font-semibold text-gray-500">{formatTransferDate(item.tarih)}</span>
                          <span className="font-bold text-sky-700">{item.fisNo}</span>
                          <span className="whitespace-normal break-words leading-snug text-gray-700">{item.aciklama || "—"}</span>
                          <span className="text-right font-bold text-gray-800">{formatTry(Number(item.tutar || 0))}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                  <p className="text-[10px] text-gray-500">{selectedTransferIds.length} havale seçildi. Açıklama tam görünür; kısaltılmaz.</p>
                  <button type="button" onClick={addSelectedTransfersToSale} className="h-9 rounded-lg bg-sky-600 px-4 text-xs font-semibold text-white hover:bg-sky-700">Seçili Havaleleri Aktar</button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-farm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-farm-border px-4 py-3 bg-white flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-full border border-farm-border text-farm-muted font-body text-xs font-semibold hover:bg-farm-cream transition-colors cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>

          <button
            type="button"
            onClick={handleSave}
            className={`flex-1 px-4 py-2 rounded-full text-white font-body text-xs font-semibold transition-colors whitespace-nowrap ${
              canSave ? "bg-farm-earth hover:bg-farm-brown cursor-pointer" : "bg-farm-earth/60 cursor-pointer"
            }`}
          >
            Satışı Kaydet
          </button>
        </div>
      </div>
    </>
  );
}
